import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Stack,
  IconButton,
  Menu,
  MenuItem,
  Link,
  CircularProgress,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  getMessages,
  sendMessage,
  updateConversationStatus,
  getConversation,
  markConversationAsRead,
} from "../../../../services/message";
import moment from "moment";

ConversationDetail.propTypes = {
  conversationId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
};

function ConversationDetail({ conversationId, onClose }) {
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [convRes, msgsRes] = await Promise.all([
          getConversation(conversationId),
          getMessages(conversationId, { order: "createdAt ASC" }),
        ]);
        setConversation(convRes.data);
        setMessages(msgsRes.data);

        // Mark conversation as read
        markConversationAsRead(conversationId).catch(console.error);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      await sendMessage(conversationId, newMessage);
      setNewMessage("");
      // Refresh messages
      const msgsRes = await getMessages(conversationId, {
        order: "createdAt ASC",
      });
      setMessages(msgsRes.data);
      // Update status if needed (e.g. automatically open if closed?)
      // For now just refresh.
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await updateConversationStatus(conversationId, status);
      const convRes = await getConversation(conversationId);
      setConversation(convRes.data);
      setAnchorEl(null);
    } catch (err) {
      console.error(err);
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 0:
        return "Nyitott";
      case 1:
        return "Lezárt";
      case 2:
        return "Alvó";
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="h6">
            {conversation?.user
              ? `${conversation.user.lastName} ${conversation.user.firstName}`
              : "Ismeretlen felhasználó"}{" "}
            (
            {conversation?.position
              ? `${conversation.position.id} - ${conversation.position.name}`
              : "Ismeretlen pozíció"}
            )
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Státusz: <strong>{getStatusLabel(conversation?.status)}</strong>
          </Typography>
          {conversation?.readByAdmin && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Utoljára olvasta: {conversation.readByAdmin.lastName}{" "}
              {conversation.readByAdmin.firstName}
            </Typography>
          )}
        </Box>
        <Box>
          <IconButton onClick={handleMenuClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={() => handleStatusChange(0)}>Megnyitás</MenuItem>
            <MenuItem onClick={() => handleStatusChange(2)}>Szundi</MenuItem>
            <MenuItem onClick={() => handleStatusChange(1)}>Lezárás</MenuItem>
          </Menu>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Messages Area */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", p: 2, bgcolor: "#f5f5f5" }}>
        <Stack spacing={2}>
          {messages.map((msg) => {
            const isAdmin = !!msg.adminUserId;
            const adminUser = msg.admin || msg.adminUser; // Handle relation naming
            const studentUser = conversation?.user;

            return (
              <Box
                key={msg.id}
                sx={{
                  display: "flex",
                  justifyContent: isAdmin ? "flex-end" : "flex-start",
                }}
              >
                <Paper
                  sx={{
                    p: 2,
                    maxWidth: "80%",
                    bgcolor: isAdmin ? "primary.light" : "white",
                    color: isAdmin ? "primary.contrastText" : "text.primary",
                    borderRadius: 2,
                  }}
                >
                  {!isAdmin && (
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ mb: 0.5, fontWeight: "bold" }}
                    >
                      {studentUser ? (
                        <Link
                          href={`/users/md/${studentUser.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          color="inherit"
                          underline="hover"
                        >
                          {studentUser.lastName} {studentUser.firstName}
                        </Link>
                      ) : (
                        "Jelentkező"
                      )}
                    </Typography>
                  )}
                  {isAdmin && (
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ mb: 0.5, fontWeight: "bold", color: "inherit" }}
                    >
                      {adminUser
                        ? `${adminUser.lastName} ${adminUser.firstName}`
                        : "Admin"}
                    </Typography>
                  )}
                  <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                    {msg.content}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    align="right"
                    sx={{ mt: 1, opacity: 0.7 }}
                  >
                    {moment(msg.createdAt).format("MM.DD. HH:mm")}
                  </Typography>
                </Paper>
              </Box>
            );
          })}
          <div ref={messagesEndRef} />
        </Stack>
      </Box>

      {/* Input Area */}
      <Box
        sx={{ p: 2, borderTop: 1, borderColor: "divider", bgcolor: "white" }}
      >
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder="Írjon üzenetet..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <Button
            variant="contained"
            color="primary"
            endIcon={
              sending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SendIcon />
              )
            }
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
          >
            Küldés
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}

export default ConversationDetail;
